import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  deepClone,
  FileHelpers,
  FileSizePipe,
  NotificationService,
  ResultWrapper,
} from '@seahorse/common';
import {
  CustomDataBaseModel,
  FieldDefinitionModel,
  FileModel,
  FileUpdateModel,
  FileService,
  ObjectDefinitionModel,
  PreferenceDataService,
  PreferenceModel,
} from '@seahorse/domain';
import { IdentityServiceBase } from '@seahorse/auth';
import {
  AreYouSureModalComponent,
  FolderTreeNode,
  FolderTreeTableComponent,
} from '@seahorse/ui';
import JSZip from 'jszip';
import { forkJoin, Observable, of, Subject, Subscription } from 'rxjs';
import { takeUntil, map, catchError } from 'rxjs/operators';
import { FileDragDropDirective } from '../file-drag-drop.directive';
import { BaseFilesCardService } from '../files-card.service';
import { FileFilterOption } from '../file-filter-option.enum';
import { FormsModule } from '@angular/forms';
import { ModalAction, ModalResponse, ModalService } from '../../modal/_index';
import { PhotoGalleryModalComponent } from '../photo-gallery-modal/photo-gallery-modal.component';
import { FolderStructureDataService } from '../../folder-structure/services/folder-structure-data.service';
import {
  FolderStructureFormComponent,
  FolderStructureModel,
} from '../../folder-structure/_index';
import { buildFolderTreeFromPaths } from '../../folder-structure/utils/folder-structure.utils';
import { FileListCardSettingsComponent } from './file-list-card-settings/file-list-card-settings.component';
import { PreferenceFieldType } from '@seahorse/domain';

@Component({
  selector: 'temp-file-list-card',
  templateUrl: 'file-list-card.component.html',
  styles: [
    `
      .bg-inbox {
        background-color: antiquewhite;
      }
    `,
  ],
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    FileDragDropDirective,
    FileSizePipe,
    FormsModule,
    FolderTreeTableComponent,
  ],
})
export class FileListCardComponent implements OnInit, OnDestroy {
  @Input() title?: string;
  @Input() archive?: string;
  @Input() archiveLocally?: boolean;
  @Input() objectType: string;

  private _objectId: string;
  @Input() set objectId(val: string) {
    this._objectId = val;
    this.loadFiles();
  }
  get objectId(): string {
    return this._objectId;
  }
  @Input() object: CustomDataBaseModel;
  @Input() systemCode: string;
  @Input() isFileDroppedFromPage?: boolean;
  @Input() objectFieldDefinitions?: Record<string, FieldDefinitionModel>;
  @Input() linkedObjectsIds?: any;
  @Input() readonly = false;
  @Input() inbox = false;
  @Input() changePrivacyOption = true;
  @Input() showImageFilter = false;
  @Input() docsOnly = false;

  @Input() childLinkedObjects?: Record<string, { data: CustomDataBaseModel[] }>;
  @Input() linkedObjectDefinitions?: Record<string, ObjectDefinitionModel>;

  private destroy$ = new Subject<void>();

  files: FileModel[];
  childObjectFiles: Record<string, FileModel[]> = {};
  defaultFolders: FolderStructureModel[] = [];
  folders: FolderTreeNode[] = [];
  openPaths: FolderTreeNode[] | null = null;
  matchingLinkedObjects?: any = [];
  loading: boolean;
  filterImages = false;
  FileFilterOption = FileFilterOption;
  initialFolder: string | null | undefined = undefined;
  filesPath: string;
  imagesPath: string;

  includeChildDocuments = false;
  includeChildDocumentsPreference: PreferenceModel | null = null;

  get showChildDocumentsOption(): boolean {
    return !!(
      this.childLinkedObjects &&
      this.linkedObjectDefinitions &&
      this.systemCode
    );
  }

  get filesOptionsFeature(): boolean {
    return this._identityService.hasFeature('fileOptions');
  }

  private get includeChildDocumentsPreferenceName(): string {
    return `child_docs_${this.systemCode}`;
  }

  private _subscription = new Subscription();

  private imageTypes = [
    'avif',
    'bmp',
    'gif',
    'jpg',
    'jpeg',
    'png',
    'tif',
    'tiff',
    'wmf',
    'emf',
    'svg',
    'webp',
    'image',
  ];

  private _modalService = inject(ModalService);
  private _translateService = inject(TranslateService);
  private _notificationService = inject(NotificationService);
  private _folderStructureDataService = inject(FolderStructureDataService);
  private _preferenceDataService = inject(PreferenceDataService);
  private _identityService = inject(IdentityServiceBase);

  constructor(
    private _filesService: FileService,
    public componentService: BaseFilesCardService
  ) {
    this.title = 'files.fileListCard.title';
    this.archive = null;
    this.archiveLocally = false;
    this.objectType = null;
    this.objectId = null;
    this.files = [];
    this.loading = false;
    this.isFileDroppedFromPage = false;

    this._filesService.objectFiles$
      .pipe(takeUntil(this.destroy$))
      .subscribe((filesData) => {
        if (filesData.length !== 0 && filesData.source === 'files') {
          componentService.filesAdd(this, filesData, 'files');
        }
      });

    componentService.reload$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.loadFiles();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this._subscription.unsubscribe();
  }

  ngOnInit() {
    this.filesPath = this._translateService.instant('shared.terms.files');
    this.imagesPath = this._translateService.instant('shared.terms.images');

    if (this.showChildDocumentsOption && this.filesOptionsFeature) {
      this.loadPreference();
    }
    this.loadFiles();
    this.filterImages = this.showImageFilter;
  }

  private loadPreference() {
    this.includeChildDocumentsPreference = this._identityService
      .getPreferences()
      .find(
        (preference) =>
          this.includeChildDocumentsPreferenceName.toLowerCase() ===
          preference.name.toLowerCase()
      );

    if (this.includeChildDocumentsPreference) {
      if (this.includeChildDocumentsPreference.fieldValue !== null) {
        this.includeChildDocuments =
          this.includeChildDocumentsPreference.fieldValue === true;
      }
    }
  }

  private loadFiles() {
    if (this.loading) return;

    if (this.objectType && this.objectId) {
      this.loading = true;

      const files$ = this.docsOnly
        ? this._filesService.getDocs(this.objectId, this.objectType, 0, 1000)
        : this._filesService.getFiles(this.objectId, this.objectType, 0, 1000);

      this.defaultFolders = [];
      const objectMappingKey = this.objectType
        .split('_')
        .slice(0, -1)
        .join('_');
      const folderStructure$ =
        this._folderStructureDataService.get(objectMappingKey);

      const childFilesObservables = this.buildChildFilesObservables();

      const allObservables$ =
        childFilesObservables.length > 0
          ? forkJoin([files$, folderStructure$, ...childFilesObservables])
          : forkJoin([files$, folderStructure$]);

      this._subscription.add(
        allObservables$.subscribe({
          next: (results: any[]) => {
            const [filesResult, folderResult, ...childResults] = results;

            if (filesResult?.hasResult) {
              this.files = filesResult.result || [];
            } else {
              this.files = [];
            }

            if (folderResult?.hasResult) {
              this.defaultFolders = folderResult.result || [];
            }

            if (childResults && childResults.length > 0) {
              this.mergeChildFiles(childResults);
            }

            this.componentService.setMatchingLinkedObjects(
              this.files,
              this.linkedObjectsIds
            );

            if (this.inbox && this.systemCode) {
              this.loadInboxFiles();
            }

            this.buildFolderTree();
            this.loading = false;
          },
          error: (e) => {
            this._notificationService.displayErrorNotification(e);
            this.loading = false;
          },
        })
      );
    }
  }

  private buildChildFilesObservables(): Observable<{
    key: string;
    files: FileModel[];
    definition: ObjectDefinitionModel;
  }>[] {
    const observables: Observable<{
      key: string;
      files: FileModel[];
      definition: ObjectDefinitionModel;
    }>[] = [];

    if (!this.showChildDocumentsOption || !this.includeChildDocuments) {
      return observables;
    }

    Object.keys(this.childLinkedObjects).forEach((key) => {
      const childObjects = this.childLinkedObjects[key];
      const definition = this.linkedObjectDefinitions[key];
      const fieldDefinition = this.objectFieldDefinitions?.[key];

      if (!childObjects || !childObjects.data || !definition) {
        return;
      }

      const dataArray = Array.isArray(childObjects.data)
        ? childObjects.data
        : [childObjects.data];

      const folderName = fieldDefinition?.name || definition.name;

      dataArray.forEach((childObj) => {
        const childObjectType = `${definition.mappingKey}_id`;
        const childObjectId =
          childObj.__Id || (this.object && this.object[key]);

        if (!childObjectId || !childObjectType) {
          return;
        }

        const childFiles$ = this.docsOnly
          ? this._filesService.getDocs(childObjectId, childObjectType, 0, 1000)
          : this._filesService.getFiles(
              childObjectId,
              childObjectType,
              0,
              1000
            );

        observables.push(
          childFiles$.pipe(
            map((result) => ({
              key,
              files: this.mapChildFiles(
                result,
                childObj.__DisplayName,
                folderName
              ),
              definition,
            })),
            catchError(() => of({ key, files: [], definition }))
          )
        );
      });
    });

    return observables;
  }

  private mapChildFiles(
    result: ResultWrapper<FileModel[]>,
    displayName: string | undefined,
    folderName: string
  ): FileModel[] {
    if (!result?.hasResult || !result.result) {
      return [];
    }

    return result.result.map((f) => {
      f.folder = displayName
        ? `${folderName}/${displayName}${f.folder ? '/' + f.folder : ''}`
        : `${folderName}${f.folder ? '/' + f.folder : ''}`;

      return f;
    });
  }

  private mergeChildFiles(childResults: any[]): void {
    if (!childResults || childResults.length === 0) {
      return;
    }

    const allChildFiles: FileModel[] = [];
    childResults.forEach((childResult) => {
      if (
        childResult?.files &&
        Array.isArray(childResult.files) &&
        childResult.files.length > 0
      ) {
        allChildFiles.push(...childResult.files);
      }
    });

    if (allChildFiles.length > 0) {
      this.files = [...this.files, ...allChildFiles];
    }
  }

  private loadInboxFiles() {
    this.loading = true;

    this._subscription.add(
      this._filesService
        .getFiles(
          this.objectId,
          `$customcontentinbox_ct-${this.systemCode}_id`,
          0,
          1000
        )
        .subscribe(
          (r: ResultWrapper<FileModel[]>) => {
            if (r.hasResult) {
              r.result.forEach((file) => {
                file.isInbox = true;
              });
              this.files = [...this.files, ...r.result];
              this.buildFolderTree();
            } else {
              this._notificationService.displayErrorNotification(r.messages);
            }
          },
          () => (this.loading = false)
        )
    );
  }

  fileIcon(fileType: string): string {
    return this._filesService.getFileTypeIcon(fileType.replace('.', ''));
  }

  fileTypes(fileFilterOption: FileFilterOption): FileModel[] {
    if (this.docsOnly) return this.files;

    switch (fileFilterOption) {
      case FileFilterOption.Files:
        return this.files.filter(
          (file) =>
            !this.imageTypes.includes(
              file.fileType.replace('.', '').toLowerCase()
            )
        );
      case FileFilterOption.Images:
        return this.files.filter((file) =>
          this.imageTypes.includes(file.fileType.replace('.', '').toLowerCase())
        );
      case FileFilterOption.All:
      default:
        return this.files;
    }
  }

  fileUploaded(uploadedFile) {
    if (this.readonly) {
      return;
    }

    if (uploadedFile) {
      this.files.push(uploadedFile);
    }
  }

  filesZip() {
    if (this.archive && this.files && this.files.length) {
      this._notificationService.showInfo(
        `${this._translateService.instant('shared.terms.pleaseWait')}...`,
        this._translateService.instant('shared.terms.compressing'),
        0
      );

      if (this.archiveLocally) {
        const zip: JSZip = new JSZip();
        let count = 0;

        this.files.forEach((x) => {
          this._filesService.getFileById(x.id).subscribe({
            next: (r: ResultWrapper<FileModel>) => {
              count++;
              zip.file(
                r.result.name + r.result.fileType,
                new Blob(
                  [this._filesService.base64ToArrayBuffer(r.result.content)],
                  { type: r.result.mimeType }
                )
              );
            },
            error: (e) => {
              this._notificationService.displayErrorNotification(e);
            },
            complete: () => {
              this._notificationService.clear();

              if (count === this.files.length) {
                zip.generateAsync({ type: 'blob' }).then((content: Blob) => {
                  const link: HTMLAnchorElement = document.createElement('a');
                  link.href = window.URL.createObjectURL(content);
                  link.download = this.archive
                    ? `${this.archive}.zip`
                    : `${this.objectType}+${this.objectId}.zip`;

                  link.click();
                });
              }
            },
          });
        });
      } else {
        this._filesService
          .zipFiles(this.objectType, this.objectId, this.archive)
          .subscribe({
            error: (e) => {
              this._notificationService.displayErrorNotification(e);
            },
            complete: () => this._notificationService.clear(),
          });
      }
    }
  }

  fileDownload(file: FileModel) {
    this.componentService.getFileById(file).subscribe({
      next: (r: ResultWrapper<FileModel>) => {
        if (r.hasResult) {
          const blob: Blob = new Blob(
            [this._filesService.base64ToArrayBuffer(r.result.content)],
            { type: r.result.mimeType }
          );
          const link: HTMLAnchorElement = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = r.result.name + r.result.fileType;

          link.click();
        } else {
          this._notificationService.displayErrorNotification(r.messages);
        }
      },
      error: (e) => {
        this._notificationService.displayErrorNotification(e);
      },
    });
  }

  filePreview(file: FileModel) {
    if (
      this.imageTypes.includes(file.fileType.replace('.', '').toLowerCase())
    ) {
      this._subscription.add(
        this._modalService
          .openModal(
            PhotoGalleryModalComponent,
            {
              file: deepClone(file),
              readonly: this.readonly || this.inbox,
              changePrivacyOption: this.changePrivacyOption || !this.inbox,
            },
            {
              size: 'lg',
              backdrop: 'static',
              keyboard: false,
            }
          )
          .subscribe({
            next: (response: ModalResponse<{ file: FileModel }>) => {
              if (response.action === ModalAction.Confirm) {
                const fileIndex = this.files.findIndex(
                  (x) => x.id === response.componentRef.file.id
                );

                if (fileIndex !== -1)
                  this.files[fileIndex] = response.componentRef.file;
              }

              if (response.action === ModalAction.Delete) {
                const fileIndex = this.files.findIndex(
                  (x) => x.id === response.componentRef.file.id
                );

                if (fileIndex !== -1)
                  this.files = this.files.filter(
                    (x) => x.id !== response.componentRef.file.id
                  );
              }
            },
          })
      );
    } else {
      this._subscription.add(
        this._filesService.getFileById(file.id).subscribe((file) => {
          if (file.hasResult) {
            FileHelpers.openDocumentBlob(
              this._filesService.base64ToArrayBuffer(file.result.content),
              file.result.mimeType
            );
          } else {
            this._notificationService.displayErrorNotification(file.messages);
          }
        })
      );
    }
  }

  onDeleteClick(file: FileModel) {
    this._subscription.add(
      this._modalService.openModal(AreYouSureModalComponent).subscribe((y) => {
        if (y) {
          this.fileDelete(file);
        }
      })
    );
  }

  fileDelete(file: FileModel) {
    this._filesService.deleteFile(file.id).subscribe({
      next: (r: ResultWrapper<FileModel>) => {
        if (r.hasResult) {
          this._notificationService.showSuccess(
            this._translateService.instant(
              'customModules.orderRegistrations.fileDeleted'
            ),
            this._translateService.instant('shared.terms.success')
          );
          this.files = this.files.filter((x) => x.id !== r.result.id);
          this.buildFolderTree();
        } else {
          this._notificationService.displayErrorNotification(r.messages);
        }
      },
      error: (e) => {
        this._notificationService.displayErrorNotification(e);
      },
    });
  }

  updatePrivacy(file: FileModel) {
    file.isPrivate = !file.isPrivate;

    const fileUpdateModel = FileUpdateModel.fromFileModel(file);

    this._filesService
      .updateFile(fileUpdateModel)
      .subscribe((r: ResultWrapper<FileModel>) => {
        if (r.hasResult) {
          this._notificationService.showSuccess(
            this._translateService.instant('files.model.changedPrivacy'),
            this._translateService.instant('shared.terms.success')
          );
        } else {
          this._notificationService.displayErrorNotification(r.messages);
        }
      });
  }

  updateFolder(file: FileModel, folder: string | null) {
    file.folder = folder;
    const fileUpdateModel = FileUpdateModel.fromFileModel(file);

    this.loading = true;
    this._subscription.add(
      this._filesService.updateFile(fileUpdateModel).subscribe({
        next: (r: ResultWrapper<FileModel>) => {
          if (!r.hasResult) {
            this._notificationService.displayErrorNotification(r.messages);
          }
        },
        error: (e) =>
          this._notificationService.displayErrorNotification(e.messages),
        complete: () => {
          this.buildFolderTree();
          this.loading = false;
        },
      })
    );
  }

  addFiles(initialFolder?: string) {
    this.initialFolder = initialFolder;
    this.componentService.filesAdd(this, null, 'files');
  }

  buildFolderTree() {
    const defaultFolderNames =
      this.defaultFolders?.map((x) => x.folderName) ?? [];
    const fileFolders: string[] = [];
    const imageFolders: string[] = [];

    const addDefaultFoldersToFiles = () => {
      defaultFolderNames.forEach((path) =>
        fileFolders.push(`${this.filesPath}/${path}`)
      );
    };

    const addFoldersWithFilesOrImages = (allPaths: Set<string>) => {
      allPaths.forEach((path) => {
        if (this.files.some((f) => f.folder === path && !this.isImage(f))) {
          if (!fileFolders.includes(`${this.filesPath}/${path}`)) {
            fileFolders.push(`${this.filesPath}/${path}`);
          }
        }
        if (this.files.some((f) => f.folder === path && this.isImage(f))) {
          imageFolders.push(`${this.imagesPath}/${path}`);
        }
      });
    };

    const addRootNodes = () => [
      ...(this.files.some((f) => !f.folder && !this.isImage(f))
        ? [this.filesPath]
        : []),
      ...(this.files.some((f) => !f.folder && this.isImage(f))
        ? [this.imagesPath]
        : []),
    ];

    if (this.filterImages) {
      addDefaultFoldersToFiles();

      const allPaths = new Set([
        ...defaultFolderNames,
        ...(this.files.map((x) => x.folder).filter(Boolean) as string[]),
      ]);

      addFoldersWithFilesOrImages(allPaths);

      const paths = [...fileFolders, ...imageFolders, ...addRootNodes()];

      this.folders = buildFolderTreeFromPaths(paths);
      return;
    }

    const allPaths = new Set([
      ...defaultFolderNames,
      ...(this.files.map((x) => x.folder).filter(Boolean) as string[]),
    ]);

    this.folders = buildFolderTreeFromPaths(Array.from(allPaths));
  }

  getFilesForFolder(
    selectedFolderPath: FolderTreeNode | null,
    rootFiles = false
  ): FileModel[] {
    if (!this.files?.length) {
      return [];
    }

    if (rootFiles) {
      return this.files.filter((f) => !f.folder);
    }

    if (!this.openPaths?.length || !selectedFolderPath) {
      return [];
    }

    if (!this.openPaths.some((x) => x.path === selectedFolderPath.path)) {
      return [];
    }

    const isFileInFolder = (f: FileModel): boolean => {
      if (!this.filterImages) {
        return f.folder === selectedFolderPath.path;
      }

      if (selectedFolderPath.path.startsWith(this.filesPath)) {
        return (
          !this.isImage(f) &&
          (`${this.filesPath}/${f.folder}` === selectedFolderPath.path ||
            (!f.folder && selectedFolderPath.path === this.filesPath))
        );
      }

      if (selectedFolderPath.path.startsWith(this.imagesPath)) {
        return (
          this.isImage(f) &&
          (`${this.imagesPath}/${f.folder}` === selectedFolderPath.path ||
            (!f.folder && selectedFolderPath.path === this.imagesPath))
        );
      }

      return false;
    };

    return this.files.filter(isFileInFolder);
  }

  openChangeFolderModal(file: FileModel) {
    const modalTitle = this._translateService.instant(
      'files.fileListCard.changeFolder'
    );
    this._subscription.add(
      this._modalService
        .buildModal()
        .withTitle(modalTitle)
        .withComponentBody({
          component: FolderStructureFormComponent,
          props: { initialForm: { folderName: file.folder } },
        })
        .open()
        .subscribe((response) => {
          if (response.action === ModalAction.Confirm) {
            const form = response.componentRef.form;
            const folder: string = form.get('folderName').value;
            if (!form || (form && form.invalid)) {
              form.get('folderName').markAsTouched();
            } else {
              this.updateFolder(file, folder);
              response.modalRef.close();
            }
          }
        })
    );
  }

  private isImage(file: FileModel): boolean {
    return this.imageTypes.includes(
      file.fileType.replace('.', '').toLowerCase()
    );
  }

  openSettings() {
    const modalTitle = this._translateService.instant(
      'files.fileListCard.settings'
    );

    this._subscription.add(
      this._modalService
        .buildModal()
        .withTitle(modalTitle)
        .withComponentBody({
          component: FileListCardSettingsComponent,
          props: { includeChildDocuments: this.includeChildDocuments },
        })
        .open()
        .subscribe((response) => {
          if (response.action === ModalAction.Confirm) {
            const result = response.componentRef.includeChildDocuments;
            if (result !== this.includeChildDocuments) {
              this.includeChildDocuments = result;
              this.savePreference();
              this.loadFiles();
            }
            response.modalRef.close();
          }
        })
    );
  }

  private savePreference() {
    if (!this.includeChildDocumentsPreference) {
      this.includeChildDocumentsPreference = new PreferenceModel();
      this.includeChildDocumentsPreference.name =
        this.includeChildDocumentsPreferenceName;
      this.includeChildDocumentsPreference.category = 'files';
      this.includeChildDocumentsPreference.fieldType =
        PreferenceFieldType.BooleanField;
    }

    this.includeChildDocumentsPreference.fieldValue =
      this.includeChildDocuments;

    this._subscription.add(
      this._preferenceDataService
        .save(this.includeChildDocumentsPreference)
        .subscribe({
          next: (result) => {
            if (result?.hasResult) {
              const preference = this._preferenceDataService.preferenceMap(
                result.result
              );
              this._identityService.setPreferences([preference]);
            }
          },
          error: () => {
            this._notificationService.showError(
              this._translateService.instant('shared.terms.error')
            );
          },
        })
    );
  }
}
